import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Search, MapPin, Globe, Phone, PlusCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const CATEGORIES = ["all", "Restaurant", "Retail", "Grocery", "Health", "Finance", "Technology", "Beauty", "Services", "Manufacturing", "Real Estate", "Other"];

export default function Directory() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { status: "approved" };
      if (q) params.q = q;
      if (category !== "all") params.category = category;
      const { data } = await api.get("/businesses", { params });
      setItems(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category]);

  const handleSearch = (e) => {
    e.preventDefault();
    load();
  };

  return (
    <div className="grain min-h-screen">
      <section className="max-w-7xl mx-auto px-6 sm:px-10 pt-20 pb-12" data-testid="directory-hero">
        <div className="label-eyebrow">The Directory</div>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-6">
          <h1 className="font-serif text-5xl sm:text-6xl text-bdu-cream tracking-tight max-w-3xl">
            Black-Owned Businesses. <span className="text-bdu-gold italic">Nationwide.</span>
          </h1>
          {user && user !== false && (
            <Link to="/submit-business" className="btn-gold" data-testid="submit-business-cta">
              <PlusCircle size={16} /> Submit a Business
            </Link>
          )}
        </div>
        <p className="mt-6 text-bdu-cream/60 max-w-2xl">
          Every listing is Black-owned and vetted. Spend here. Recirculate the dollar. Build the fund.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-8">
        <form onSubmit={handleSearch} className="flex flex-wrap gap-3 items-center border border-bdu-gold/15 bg-bdu-surface p-3">
          <div className="flex items-center gap-2 flex-1 min-w-[240px]">
            <Search size={16} className="text-bdu-gold" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, city, description..."
              className="bg-transparent w-full outline-none text-bdu-cream placeholder:text-bdu-cream/40 py-2"
              data-testid="directory-search-input"
            />
          </div>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="bg-bdu-bg border border-bdu-gold/20 px-3 py-2 text-sm text-bdu-cream outline-none focus:border-bdu-gold"
            data-testid="directory-category-select"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>{c === "all" ? "All Categories" : c}</option>
            ))}
          </select>
          <button className="btn-gold" type="submit" data-testid="directory-search-btn">Search</button>
        </form>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-24">
        {loading ? (
          <div className="text-bdu-cream/50 py-16 text-center">Loading businesses...</div>
        ) : items.length === 0 ? (
          <div className="relative overflow-hidden border border-bdu-gold/25 py-24 px-8 text-center" data-testid="directory-empty">
            <div className="absolute inset-0" style={{
              background: "radial-gradient(circle at center, rgba(212,175,55,0.08) 0%, rgba(0,0,0,0) 70%)"
            }} />
            <div className="relative z-10 max-w-xl mx-auto">
              <div className="label-eyebrow" style={{ color: "rgba(212,175,55,0.9)" }}>Now Enrolling · Black-Owned Businesses</div>
              <h3 className="mt-6 font-serif text-4xl sm:text-5xl text-bdu-cream leading-tight">
                Be the first name on the list.
              </h3>
              <p className="mt-6 text-bdu-cream/70 leading-relaxed">
                We're accepting founding businesses to seed the directory. Every listing gets verified.
                Once approved, your business becomes part of the trusted circle donors are pointed to spend within.
              </p>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                {user && user !== false ? (
                  <Link to="/submit-business" className="btn-gold" data-testid="directory-empty-submit">
                    <PlusCircle size={16} /> Submit Your Business
                  </Link>
                ) : (
                  <>
                    <Link to="/register?next=/submit-business" className="btn-gold">
                      Create Account to Submit
                    </Link>
                    <Link to="/login?next=/submit-business" className="btn-ghost">
                      Login
                    </Link>
                  </>
                )}
              </div>
              <div className="mt-10 pt-8 border-t border-bdu-gold/15 grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="font-serif text-3xl text-bdu-gold">FREE</div>
                  <div className="mt-1 text-[0.65rem] uppercase tracking-widest text-bdu-cream/50">To List</div>
                </div>
                <div>
                  <div className="font-serif text-3xl text-bdu-gold">24-48h</div>
                  <div className="mt-1 text-[0.65rem] uppercase tracking-widest text-bdu-cream/50">Review</div>
                </div>
                <div>
                  <div className="font-serif text-3xl text-bdu-gold">$50M</div>
                  <div className="mt-1 text-[0.65rem] uppercase tracking-widest text-bdu-cream/50">Fund Goal</div>
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="directory-grid">
            {items.map((b) => (
              <article key={b.id} className="bdu-card flex flex-col" data-testid={`business-card-${b.id}`}>
                <div className="label-eyebrow">{b.category}</div>
                <h3 className="mt-3 font-serif text-2xl text-bdu-cream">{b.name}</h3>
                <div className="mt-2 flex items-start gap-2 text-sm text-bdu-cream/50">
                  <MapPin size={14} className="mt-0.5 shrink-0" />
                  <div>
                    {b.address && <div className="text-bdu-cream/65">{b.address}</div>}
                    <div>{b.city}, {b.state}</div>
                  </div>
                </div>
                <p className="mt-4 text-sm text-bdu-cream/70 leading-relaxed flex-1">{b.description}</p>
                <div className="mt-6 pt-4 border-t border-bdu-gold/10 space-y-2 text-xs text-bdu-cream/60">
                  {b.website && (
                    <a href={b.website.startsWith("http") ? b.website : `https://${b.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-bdu-gold">
                      <Globe size={12} /> {b.website}
                    </a>
                  )}
                  {b.phone && (
                    <div className="flex items-center gap-2"><Phone size={12} /> {b.phone}</div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
